import { useEffect, useState } from 'react';
import { Bot, Grid2X2, Lock, Maximize2, RotateCw, Users, X } from 'lucide-react';
import { AuthGate } from '@/auth/AuthGate';
import { AuthProvider, useAuth } from '@/auth/AuthProvider';
import { completeFiloLogout } from '@/auth/logout';
import { FiloRemoteProvider, useFiloRemote } from '@/connection/FiloRemoteContext';
import { RobotCameraPreview, type CameraFloatingLayout } from '@/components/camera/RobotCameraPreview';
import { useRobotCameraPreview } from '@/components/camera/useRobotCameraPreview';
import { RobotConnectionPanel } from '@/components/robots/RobotConnectionPanel';
import { UserProfileMenu } from '@/components/account/UserProfileMenu';
import { OfflineAudioCreation } from '@/components/account/OfflineAudioDialog';
import { IntroductionCreation } from '@/components/account/IntroductionDialog';
import { FlowEditor, type DuplicateFlowRequest } from '@/flow/FlowEditor';
import { FlowPreviewOverlay } from '@/flow/FlowPreviewOverlay';
import type { SharedFlowSnapshot } from '@/flow/realtimeFlow';
import type { FlowDisplayLanguage } from '@/flow/displayLanguage';
import {
  DEFAULT_REMOTE_FLOW_VIEW_MODE,
  RemoteFlowViewer,
  type RemoteFlowViewMode,
} from '@/flow/RemoteFlowViewer';
import { ChatAppLauncher } from '@/apps/chat/ChatAppLauncher';
import { ChatAppOverlay } from '@/apps/chat/ChatAppOverlay';
import { MazeAppLauncher } from '@/apps/maze/MazeAppLauncher';
import { MazeAppOverlay } from '@/apps/maze/MazeAppOverlay';
import { LearningAppLauncher } from '@/apps/learning/LearningAppLauncher';
import { LearningAppOverlay } from '@/apps/learning/LearningAppOverlay';
import { DEFAULT_SAY_TEXT_SPEAKER } from '@/flow/config/sayTextVoice';
import { OFFLINE_MODE } from '@/config/offline';

function FiloWorkspace() {
  const auth = useAuth();
  const remote = useFiloRemote();
  const [activeDirectory, setActiveDirectory] = useState<'robots' | 'users' | 'apps' | null>(null);
  const [flowPreviewOpen, setFlowPreviewOpen] = useState(false);
  const [flowPreviewSnapshot, setFlowPreviewSnapshot] = useState<SharedFlowSnapshot | null>(null);
  const [flowDisplayLanguage, setFlowDisplayLanguage] = useState<FlowDisplayLanguage>('en');
  const [flowContext, setFlowContext] = useState<'edit' | 'simulation' | 'robots'>('edit');
  const [chatRobotId, setChatRobotId] = useState<string | null>(null);
  const [chatSpeaker, setChatSpeaker] = useState(DEFAULT_SAY_TEXT_SPEAKER);
  const [mazeStageId, setMazeStageId] = useState<number | null>(null);
  const [learningProblemId, setLearningProblemId] = useState<string | null>(null);
  const [learningRefreshKey, setLearningRefreshKey] = useState(0);
  const [cameraViewMode, setCameraViewMode] = useState<'docked' | 'floating'>('docked');
  const [remoteFlowViewMode, setRemoteFlowViewMode] = useState<RemoteFlowViewMode>(DEFAULT_REMOTE_FLOW_VIEW_MODE);
  const [remoteSplitRatio, setRemoteSplitRatio] = useState(50);
  const [duplicateFlowRequest, setDuplicateFlowRequest] = useState<DuplicateFlowRequest | null>(null);
  // 消音状態とフローティングの配置は表示切替（再マウント）をまたいで引き継ぐ
  const [cameraAudioMuted, setCameraAudioMuted] = useState(false);
  const [cameraFloatingLayout, setCameraFloatingLayout] = useState<CameraFloatingLayout>({
    position: null,
    width: null,
  });
  const [isNarrowViewport, setNarrowViewport] = useState(
    () => window.matchMedia('(max-width: 520px)').matches,
  );
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [offlineAudioOpen, setOfflineAudioOpen] = useState(() => {
    try { return OFFLINE_MODE && sessionStorage.getItem('filo:audio-creation:return') === '1'; }
    catch { return false; }
  });
  const [introductionOpen, setIntroductionOpen] = useState(() => {
    try { return OFFLINE_MODE && sessionStorage.getItem('filo:audio-creation:return') === 'introduction'; }
    catch { return false; }
  });
  const hasRobotAlert =
    Object.values(remote.snapshot.sessions).some(
      ({ state }) => state === 'reconnecting' || state === 'offline' || state === 'failed',
    )
    || remote.directoryState === 'reconnecting'
    || remote.directoryState === 'failed';

  const workspaceContext = remote.userFlow.selectedUser ? 'compare' : flowContext;
  useEffect(() => {
    if (!activeDirectory) return;
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveDirectory(null);
    };
    window.addEventListener('keydown', closeWithEscape);
    return () => window.removeEventListener('keydown', closeWithEscape);
  }, [activeDirectory]);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 520px)');
    const onChange = (event: MediaQueryListEvent) => setNarrowViewport(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  const cameraPreview = useRobotCameraPreview({
    representativeRobotId: remote.snapshot.representativeRobotId,
    setRepresentative: remote.setRepresentative,
    acquireRepresentativeCamera: remote.acquireRepresentativeCamera,
    migrateRepresentativeCamera: remote.migrateRepresentativeCamera,
    releaseCamera: remote.releaseCamera,
    setRobotMicrophone: async (robotId, enabled) => {
      if (remote.setRobotMicrophone) {
        await remote.setRobotMicrophone(robotId, enabled);
        return;
      }
      await remote.callRobot(robotId, enabled ? 'micOn' : 'micOff');
    },
  });
  const previewRobot = remote.robots.find(({ robotId }) => robotId === cameraPreview.state.robotId);
  const cameraActive = cameraPreview.state.status !== 'off';
  const cameraStream = cameraPreview.state.status === 'stopping' || cameraPreview.state.status === 'off'
    ? null
    : remote.representativeStream;
  const cameraRobotName = previewRobot?.displayName ?? cameraPreview.state.robotId ?? '代表ロボット';
  const cameraDocked = cameraActive && cameraViewMode === 'docked' && !isNarrowViewport;
  const chatRobot = remote.robots.find(({ robotId }) => robotId === chatRobotId);

  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError(null);
    try {
      await completeFiloLogout(remote.disposeAll, auth.signOut);
    } catch (error) {
      setLogoutError(error instanceof Error ? error.message : String(error));
      setLoggingOut(false);
    }
  };

  return (
    <div className="filo-app">
      <section className="portrait-orientation-guard" role="status" aria-label="横向き表示の案内">
        <RotateCw aria-hidden="true" size={38} strokeWidth={1.8} />
        <h1>端末を横向きにしてください</h1>
        <p>Filoの編集画面は横向きで利用できます。</p>
      </section>
      <main className="workspace-layout">
        <section
          className={`flow-workspace${remote.userFlow.selectedUser && remoteFlowViewMode === 'split' ? ' is-remote-split' : ''}`}
          data-workspace-context={workspaceContext}
          style={remote.userFlow.selectedUser && remoteFlowViewMode === 'split'
            ? { gridTemplateRows: `${remoteSplitRatio}% ${100 - remoteSplitRatio}%` }
            : undefined}
          aria-label="フローエディタ"
        >
          <nav className="workspace-directory-controls" aria-label="一覧">
            <button
              type="button"
              onClick={() => setActiveDirectory('robots')}
              aria-label={remote.connectedCount > 1
                ? `ロボット一覧を開く（${remote.connectedCount}台接続中）`
                : 'ロボット一覧を開く'}
              title="ロボット一覧"
              className={`workspace-directory-controls__button${remote.connectedCount > 0 ? ' is-connected' : ''}`}
            >
              <Bot aria-hidden="true" />
              <span className="workspace-directory-controls__label">ロボット</span>
              {hasRobotAlert && <span className="workspace-directory-controls__alert" aria-label="接続状態を確認" />}
              {remote.connectedCount > 1 && (
                <span className="workspace-directory-controls__badge" aria-hidden="true">
                  {remote.connectedCount}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveDirectory('users')}
              aria-label={OFFLINE_MODE ? 'ユーザー一覧（オフラインでは使用できません）' : 'ユーザー一覧を開く'}
              title={OFFLINE_MODE ? 'オフラインではユーザー共有は使用できません' : 'ユーザー一覧'}
              disabled={OFFLINE_MODE}
              className={`workspace-directory-controls__button${OFFLINE_MODE ? ' is-unavailable' : ''}`}
            >
              <Users aria-hidden="true" />
              <span className="workspace-directory-controls__label">ユーザー</span>
              {OFFLINE_MODE && (
                <span className="workspace-directory-controls__lock" aria-hidden="true">
                  <Lock />
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveDirectory('apps')}
              aria-label="アプリ一覧を開く"
              title="アプリ一覧"
              className="workspace-directory-controls__button"
            >
              <Grid2X2 aria-hidden="true" />
              <span className="workspace-directory-controls__label">アプリ</span>
            </button>
            <button
              type="button"
              onClick={() => setFlowPreviewOpen(true)}
              disabled={!flowPreviewSnapshot}
              aria-label="フローチャートをプレビュー"
              title="フローチャートをプレビュー"
              className="workspace-directory-controls__button"
            >
              <Maximize2 aria-hidden="true" />
              <span className="workspace-directory-controls__label">プレビュー</span>
            </button>
          </nav>
          <div className="flow-workspace__editor">
            {remote.userFlow.selectedUser && remoteFlowViewMode === 'split' && (
              <div className="flow-workspace__self-label">自分のフロー</div>
            )}
            <FlowEditor
            duplicateFlowRequest={duplicateFlowRequest}
            onWorkspaceContextChange={setFlowContext}
            onFlowSnapshotChange={setFlowPreviewSnapshot}
            onDisplayLanguageChange={setFlowDisplayLanguage}
            toolbarSlots={{
              brand: (
                <div className="flex shrink-0 items-center gap-2">
                  <div className="filo-mark filo-mark--small" aria-hidden="true">
                    <span /><span /><span />
                  </div>
                  <h1 className="text-lg font-bold tracking-tight max-[520px]:sr-only">Filo</h1>
                  {OFFLINE_MODE && <span className="text-xs font-semibold text-amber-800">USB オフライン</span>}
                </div>
              ),
              trailing: (
                <div className="relative flex shrink-0 items-center gap-2">
                  {auth.user && (
                    <UserProfileMenu
                      user={auth.user}
                      loggingOut={loggingOut}
                      error={logoutError}
                      onNicknameChange={auth.setNickname}
                      onLogout={() => void logout()}
                      onPrecreate={() => setOfflineAudioOpen(true)}
                      onIntroduction={() => setIntroductionOpen(true)}
                    />
                  )}
                </div>
              ),
            }}
            robotCameraView={{
              active: cameraActive,
              docked: cameraDocked,
              render: ({ hidden }) => (
                <RobotCameraPreview
                  variant="docked"
                  state={cameraPreview.state}
                  stream={cameraStream}
                  robotName={cameraRobotName}
                  rotated
                  defaultAudioMuted={cameraAudioMuted}
                  suspendAudio={hidden}
                  onAudioMutedChange={setCameraAudioMuted}
                  onExpand={() => setCameraViewMode('floating')}
                  onClose={() => void cameraPreview.closeCamera()}
                  onRetry={() => void cameraPreview.retryCamera()}
                  onPlaying={cameraPreview.markStreamPlaying}
                  onWaiting={cameraPreview.markStreamWaiting}
                  onError={cameraPreview.reportStreamError}
                />
              ),
            }}
            />
          </div>
          {OFFLINE_MODE && offlineAudioOpen && auth.user && (
            <OfflineAudioCreation userId={auth.user.sub} onClose={() => setOfflineAudioOpen(false)} />
          )}
          {OFFLINE_MODE && introductionOpen && auth.user && (
            <IntroductionCreation userId={auth.user.sub} onClose={() => setIntroductionOpen(false)} />
          )}
          {flowPreviewOpen && flowPreviewSnapshot && (
            <FlowPreviewOverlay
              snapshot={flowPreviewSnapshot}
              displayLanguage={flowDisplayLanguage}
              onClose={() => setFlowPreviewOpen(false)}
            />
          )}
          {remote.userFlow.selectedUser && (
            <RemoteFlowViewer
              displayLanguage={flowDisplayLanguage}
              displayName={remote.userFlow.selectedUser.displayName}
              state={remote.userFlow.connectionState}
              snapshot={remote.userFlow.remoteFlow}
              error={remote.userFlow.lastError}
              mode={remoteFlowViewMode}
              splitRatio={remoteSplitRatio}
              onSplitRatioChange={setRemoteSplitRatio}
              onModeChange={setRemoteFlowViewMode}
              onDuplicate={(mode) => {
                const snapshot = remote.userFlow.remoteFlow;
                if (!snapshot) return;
                setDuplicateFlowRequest({ id: crypto.randomUUID(), mode, snapshot });
              }}
              onClose={() => void remote.selectUserFlow(null)}
            />
          )}
          {chatRobotId && (
            <ChatAppOverlay
              robotId={chatRobotId}
              robotName={chatRobot?.displayName ?? chatRobotId}
              speaker={chatSpeaker}
              onClose={() => setChatRobotId(null)}
            />
          )}
          {mazeStageId !== null && (
            <MazeAppOverlay
              stageId={mazeStageId}
              initialDisplayLanguage={flowDisplayLanguage}
              onClose={() => {
                setMazeStageId(null);
              }}
            />
          )}
          {learningProblemId !== null && (
            <LearningAppOverlay
              key={learningProblemId}
              problemId={learningProblemId}
              userSub={auth.user?.sub ?? 'anonymous'}
              displayLanguage={flowDisplayLanguage}
              onNavigate={setLearningProblemId}
              onClose={() => {
                setLearningProblemId(null);
                setLearningRefreshKey((current) => current + 1);
                setActiveDirectory('apps');
              }}
            />
          )}
        </section>
      </main>
      <div
        className={`workspace-directory-dialog${activeDirectory ? ' is-open' : ''}`}
        aria-hidden={!activeDirectory}
      >
        <button
          type="button"
          className="workspace-directory-dialog__backdrop"
          onClick={() => setActiveDirectory(null)}
          aria-label="ダイアログを閉じる"
          tabIndex={activeDirectory ? 0 : -1}
        />
        <section
          role="dialog"
          aria-modal={Boolean(activeDirectory)}
          aria-labelledby="workspace-directory-dialog-title"
          className={`workspace-directory-dialog__panel${activeDirectory === 'apps' ? ' is-apps' : ''}${activeDirectory === 'robots' ? ' is-robots' : ''}`}
          hidden={!activeDirectory}
        >
          <header className="workspace-directory-dialog__header">
            <h2 id="workspace-directory-dialog-title">
              {activeDirectory === 'apps'
                ? 'アプリ一覧'
                : activeDirectory === 'users'
                  ? 'ユーザー一覧'
                  : 'ロボット一覧'}
            </h2>
            <button
              type="button"
              onClick={() => setActiveDirectory(null)}
              aria-label="一覧を閉じる"
              className="workspace-directory-dialog__close"
            >
              <X aria-hidden="true" size={20} />
            </button>
          </header>
          <div className="workspace-directory-dialog__body">
            <div className="workspace-directory-dialog__robots" hidden={activeDirectory === 'apps'}>
              <RobotConnectionPanel
                cameraPreview={cameraPreview}
                initialDirectoryView={activeDirectory === 'users' ? 'users' : 'robots'}
                currentUserId={auth.user?.sub ?? null}
              />
            </div>
            <div className="workspace-directory-dialog__apps" hidden={activeDirectory !== 'apps'}>
              {!OFFLINE_MODE && <ChatAppLauncher
                onLaunch={(robotId, speaker) => {
                  void cameraPreview.closeCamera().finally(() => {
                    setChatSpeaker(speaker);
                    setChatRobotId(robotId);
                    setActiveDirectory(null);
                  });
                }}
              />}
              <LearningAppLauncher
                key={learningRefreshKey}
                userSub={auth.user?.sub ?? 'anonymous'}
                onLaunch={(problemId) => {
                  setLearningProblemId(problemId);
                  setActiveDirectory(null);
                }}
              />
              <MazeAppLauncher
                onLaunch={(stageId) => {
                  setMazeStageId(stageId);
                  setActiveDirectory(null);
                }}
              />
            </div>
          </div>
        </section>
      </div>
      {cameraActive && (cameraViewMode === 'floating' || isNarrowViewport) && (
        <RobotCameraPreview
          state={cameraPreview.state}
          stream={cameraStream}
          robotName={cameraRobotName}
          rotated
          defaultAudioMuted={cameraAudioMuted}
          defaultFloatingLayout={cameraFloatingLayout}
          onAudioMutedChange={setCameraAudioMuted}
          onFloatingLayoutChange={setCameraFloatingLayout}
          onDock={isNarrowViewport ? undefined : () => setCameraViewMode('docked')}
          onClose={() => void cameraPreview.closeCamera()}
          onRetry={() => void cameraPreview.retryCamera()}
          onPlaying={cameraPreview.markStreamPlaying}
          onWaiting={cameraPreview.markStreamWaiting}
          onError={cameraPreview.reportStreamError}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate>
        <FiloRemoteProvider>
          <FiloWorkspace />
        </FiloRemoteProvider>
      </AuthGate>
    </AuthProvider>
  );
}
